import {
  CanonicalDataType,
  isMessagePathAttr,
  MessagePathMetadataWellKnown,
} from "@/shared/domain/topics";
import type { PlotSeries } from "@/shared/state/visualization";
import {
  Duration,
  nanoSecToDate,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
} from "@/shared/time";

import {
  elapsedTimeFormatter,
  formatTimeByMode,
  TimeDisplayMode,
} from "../../../WorkspaceCtx/TimeDisplayMode";
import { resolveCategoryLabel } from "../../resolveCategoryLabel";
import { effectiveTimeDisplayMode, type SeriesAlignment } from "../alignment";
import type { HoverDatum } from "../PlotRenderer";

/**
 * Why an aligned tooltip row needs a caveat next to its values:
 * - "fallback-anchor": the panel's anchor could not be resolved for the
 *   series, so it is aligned to the start of its own recording.
 * - "missing-alignment": the series has no alignment entry at all, so it
 *   plots against the panel start rather than the shared alignment.
 */
export type AlignmentCaveat = "fallback-anchor" | "missing-alignment";

/** Everything one tooltip row displays about the datum under the cursor. */
export interface TooltipRow {
  caveat: AlignmentCaveat | undefined;
  /** The series' display label; `undefined` when the datum matches no series. */
  label: string | undefined;
  time: string;
  value: string;
}

interface BuildTooltipRowParams {
  /**
   * How each series plots in aligned mode; `undefined` when the panel plots
   * absolute time.
   */
  alignment: SeriesAlignment | undefined;
  datum: HoverDatum;
  /** The workspace's earliest time, which anchors the elapsed display modes. */
  earliestTimeNs: bigint | null;
  /**
   * Renders ElapsedTime-mode values, defaulting to
   * {@link formatElapsedSecondsAsDuration}. Injectable because
   * Intl.DurationFormat is unavailable in the Node unit-test environment.
   */
  formatElapsedSeconds?: (seconds: number) => string;
  /** Display labels for the panel's series, keyed by series id. */
  labels: Record<string, string>;
  mode: TimeDisplayMode;
  series: PlotSeries[];
}

/**
 * Resolves the datum's series and formats its row. Free of React so the time
 * formatting and the caveat rules can be tested without rendering a panel.
 */
export function buildTooltipRow({
  alignment,
  datum,
  earliestTimeNs,
  formatElapsedSeconds = formatElapsedSecondsAsDuration,
  labels,
  mode,
  series,
}: BuildTooltipRowParams): TooltipRow {
  // A series fans out to one chart dataset per array element its message path
  // traverses, each labelled `${seriesId}_${index}`, so the datum's dataset id
  // starts with the id of the series it came from.
  const rowSeries = series.find((s) => datum.datasetId.startsWith(s.id));
  return {
    caveat: alignmentCaveat(rowSeries, alignment),
    label: rowSeries === undefined ? undefined : labels[rowSeries.id],
    time: displayTime(
      datum,
      mode,
      alignment !== undefined,
      earliestTimeNs,
      formatElapsedSeconds,
    ),
    value: displayValue(datum, rowSeries),
  };
}

function alignmentCaveat(
  rowSeries: PlotSeries | undefined,
  alignment: SeriesAlignment | undefined,
): AlignmentCaveat | undefined {
  if (alignment === undefined || rowSeries === undefined) {
    return undefined;
  }
  const aligned = alignment.seriesById[rowSeries.id];
  if (aligned === undefined) {
    return "missing-alignment";
  }
  if (aligned.isFallback) {
    return "fallback-anchor";
  }
  return undefined;
}

/**
 * The row's time text. `datum.x` is elapsed seconds; `datum.logTime` is the
 * absolute timestamp.
 */
function displayTime(
  datum: HoverDatum,
  mode: TimeDisplayMode,
  isAligned: boolean,
  earliestTimeNs: bigint | null,
  formatElapsedSeconds: (seconds: number) => string,
): string {
  const effectiveMode = effectiveTimeDisplayMode(mode, isAligned);
  if (isAligned) {
    // Every session shares one aligned x position, so only the datum's own log
    // time tells them apart; show both. The parenthetical formats that log time
    // with the workspace's selected mode rather than the coerced one, matching
    // the timeline readout.
    const elapsed =
      effectiveMode === TimeDisplayMode.ElapsedTime
        ? formatElapsedSeconds(datum.x)
        : datum.x.toFixed(3);
    return `${elapsed} (${formatTimeByMode(datum.logTime, mode, earliestTimeNs)})`;
  }
  switch (effectiveMode) {
    case TimeDisplayMode.Elapsed:
      return datum.x.toFixed(3);
    case TimeDisplayMode.ElapsedTime:
      return formatElapsedSeconds(datum.x);
    case TimeDisplayMode.Log:
      return nanoSecToSecStr(datum.logTime);
    case TimeDisplayMode.UTC:
      return nanoSecToDate(datum.logTime).toISOString();
    case TimeDisplayMode.Local:
      return nanoSecToLocalTimestamp(datum.logTime);
  }
}

function displayValue(
  datum: HoverDatum,
  rowSeries: PlotSeries | undefined,
): string {
  if (rowSeries === undefined) {
    return String(datum.y);
  }

  const lastPathPart = rowSeries.data.messagePath.parts.at(-1);
  const dataType = isMessagePathAttr(lastPathPart)
    ? lastPathPart.dataType
    : CanonicalDataType.Unknown;

  if (dataType !== CanonicalDataType.Categorical) {
    return String(datum.y);
  }

  // A categorical y is an index into the message path's category labels;
  // anything those labels cannot resolve shows as the raw number.
  const categories =
    rowSeries.data.messagePath.metadata[
      MessagePathMetadataWellKnown.Categories
    ];
  if (!Array.isArray(categories) || categories.length === 0) {
    return String(datum.y);
  }

  return (
    resolveCategoryLabel(datum.y, categories as string[]) ?? String(datum.y)
  );
}

/**
 * Renders elapsed seconds with the same Intl.DurationFormat `formatTimeByMode`
 * uses for ElapsedTime, so a row's elapsed value and its parenthetical share a
 * format. Falls back to the three-decimal seconds Elapsed mode shows on
 * runtimes without Intl.DurationFormat.
 */
function formatElapsedSecondsAsDuration(seconds: number): string {
  if (!elapsedTimeFormatter) {
    return seconds.toFixed(3);
  }
  return elapsedTimeFormatter.format(Duration.from({ seconds }).toObject());
}
